import { RegistroDto, UpdateRegistroDto } from "@/api";
import { isNil } from "lodash";

export type RicettaParamConfig =
  | { suffisso: string; kind: "text"; labelKey: string }
  | {
      suffisso: string;
      kind: "number";
      labelKey: string;
      decimals?: number;
      min?: number;
      max?: number;
      unit?: string;
    };

export const RICETTA_PARAMETRI: readonly RicettaParamConfig[] = [
  {
    suffisso: "nomermod",
    kind: "text",
    labelKey: "gflTfld5502.ricette.parametri.nome",
  },
  {
    suffisso: "0300rmod",
    kind: "number",
    labelKey: "gflTfld5502.ricette.parametri.soglia0300",
    decimals: 1,
    min: 0,
    max: 100,
    unit: "%",
  },
  {
    suffisso: "sokrmod",
    kind: "number",
    labelKey: "gflTfld5502.ricette.parametri.sogliaOk",
    decimals: 1,
    min: 0,
    max: 100,
    unit: "%",
  },
  {
    suffisso: "81rmod",
    kind: "number",
    labelKey: "gflTfld5502.ricette.parametri.soglia8001000",
    decimals: 1,
    min: 0,
    max: 100,
    unit: "%",
  },
  {
    suffisso: "152rmod",
    kind: "number",
    labelKey: "gflTfld5502.ricette.parametri.soglia15002000",
    decimals: 1,
    min: 0,
    max: 100,
    unit: "%",
  },
];

const RICETTA_PREFIX = "ricetta_";
const RICETTA_REGISTRO_RE = /^ricetta_(\d+)_(.+)$/;

/**
 * Nome del campo del form, che coincide con il nome del registro e con l'ultimo
 * segmento del topic MQTT. L'indice viene usato cosi` come l'ha pubblicato il
 * PLC (stringa, non numero): se la macchina pubblicasse `ricetta_01_nomermod`,
 * ricostruire il nome da `Number("01")` scriverebbe su `ricetta_1_nomermod`,
 * cioe` su un topic diverso da quello pubblicato.
 */
export function registroRicetta(indiceRaw: string, suffisso: string) {
  return `${RICETTA_PREFIX}${indiceRaw}_${suffisso}`;
}

export type RicettaRegistroParsed = {
  indiceRaw: string;
  indice: number;
  suffisso: string;
  /** null se il suffisso non e` tra quelli di RICETTA_PARAMETRI */
  param: RicettaParamConfig | null;
};

export function parseRegistroRicetta(
  registro: string
): RicettaRegistroParsed | null {
  const match = RICETTA_REGISTRO_RE.exec(registro);
  if (!match) {
    return null;
  }

  const indice = Number(match[1]);
  if (!Number.isFinite(indice)) {
    return null;
  }

  const suffisso = match[2];

  return {
    indiceRaw: match[1],
    indice,
    suffisso,
    param: RICETTA_PARAMETRI.find((p) => p.suffisso === suffisso) ?? null,
  };
}

export type RicettaDiscovered = { indiceRaw: string; indice: number };

/**
 * Elenco delle ricette derivato dagli indici presenti nei registri ricevuti.
 * Gli indici non sono necessariamente contigui.
 */
export function discoverRicette(
  registri: RegistroDto[] | undefined
): RicettaDiscovered[] {
  const trovate = new Map<string, RicettaDiscovered>();
  const suffissiIgnoti = new Set<string>();

  for (const registro of registri ?? []) {
    const parsed = parseRegistroRicetta(registro.registro);
    if (!parsed) {
      continue;
    }

    if (!parsed.param) {
      suffissiIgnoti.add(parsed.suffisso);
      continue;
    }

    if (!trovate.has(parsed.indiceRaw)) {
      trovate.set(parsed.indiceRaw, {
        indiceRaw: parsed.indiceRaw,
        indice: parsed.indice,
      });
    }
  }

  // I suffissi dei parametri sono un'assunzione in attesa dei dati reali: se la
  // macchina ne pubblica altri va aggiornato RICETTA_PARAMETRI.
  if (process.env.NODE_ENV !== "production" && suffissiIgnoti.size > 0) {
    console.warn(
      "[GFL_TFLD_550_2] suffissi ricetta non riconosciuti, aggiorna RICETTA_PARAMETRI:",
      Array.from(suffissiIgnoti)
    );
  }

  return Array.from(trovate.values()).sort(
    (a, b) => a.indice - b.indice || a.indiceRaw.localeCompare(b.indiceRaw)
  );
}

function parseNumber(value: string | undefined) {
  if (isNil(value) || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function mapEmptyStringToSlash(value: string) {
  return value === "" ? "/" : value;
}

/**
 * Valori dei registri ricetta nel formato del form. Emette solo le chiavi dei
 * registri presenti in input: funziona sia col caricamento iniziale (tutti i
 * registri) sia col readback dopo il salvataggio (un registro alla volta).
 */
export function registriRicetteToForm(
  registri: RegistroDto[] | undefined
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const registro of registri ?? []) {
    const parsed = parseRegistroRicetta(registro.registro);
    if (!parsed?.param) {
      continue;
    }

    if (parsed.param.kind === "number") {
      result[registro.registro] = parseNumber(registro.value);
    } else {
      result[registro.registro] =
        registro.value === "/" ? "" : registro.value || "";
    }
  }

  return result;
}

/**
 * Update dei registri ricetta derivati dalle chiavi del form. I numeri vengono
 * inviati come sono (il PLC e` Eaton, non richiede la notazione scientifica
 * usata dalle macchine Siemens).
 */
export function formRicetteToRegistri(
  form: Record<string, any> | undefined,
  topicPrefix: string
): UpdateRegistroDto[] {
  const updates: UpdateRegistroDto[] = [];

  for (const registro of Object.keys(form ?? {})) {
    const parsed = parseRegistroRicetta(registro);
    if (!parsed?.param) {
      continue;
    }

    const value = form![registro];
    if (isNil(value)) {
      continue;
    }

    if (parsed.param.kind === "number") {
      updates.push({ registro, topicPrefix, value });
    } else {
      updates.push({
        registro,
        topicPrefix,
        value: mapEmptyStringToSlash(String(value)),
      });
    }
  }

  return updates;
}
